import {
  allergiesOptions,
  allergiesNameMapper,
} from "../../pages/Student/OrderPage/constants/allergies";
import {
  getStationById,
  getSubStationById,
  getDiningById,
  getMenuGroups,
  getItemById,
  getAllowedItems,
  isSuggestionRepeatable,
  splitFullName,
  NOTIFICATION_LABELS,
  cap,
} from "./chatSteps";
import { say, line, fill } from "./forklyVoice";

const groupedItemRows = (state, ctx) => {
  const groups = getMenuGroups(ctx);
  const rows = [];
  if (!state.options_id?.length) return rows;
  if (!groups.length) {
    rows.push(["Meal", `${state.options_id.length} selected`]);
    return rows;
  }
  const byGroup = new Map();
  state.options_id.forEach((id) => {
    const found = getItemById(groups, id);
    if (!found) return;
    const gName = found.group?.group_name || "Meal";
    if (!byGroup.has(gName)) byGroup.set(gName, []);
    byGroup.get(gName).push(found.item.name);
  });
  byGroup.forEach((names, gName) => rows.push([gName, names.join(", ")]));
  return rows;
};

export const buildForklySummaryRows = (state, ctx) => {
  const rows = [];
  const st = getStationById(ctx, state.food_station_id);
  if (st) rows.push(["Station", st.name]);
  if (st?.is_multi && state.sub_station_id) {
    const sub = getSubStationById(ctx, state.food_station_id, state.sub_station_id);
    if (sub) rows.push(["Counter", sub.name]);
  }
  const dining = getDiningById(ctx, state.dining_option_id);
  if (dining) rows.push(["Dining", dining.name]);

  rows.push(...groupedItemRows(state, ctx));

  const hasAllergies =
    Array.isArray(state.allergies) &&
    state.allergies.length > 0 &&
    !(state.allergies.length === 1 && state.allergies[0] === "none");
  rows.push([
    "Allergies",
    hasAllergies
      ? state.allergies.map((a) => allergiesNameMapper[a] || a).join(", ")
      : "None",
  ]);

  const fullName = `${state.name || ""} ${state.surname || ""}`.trim();
  if (fullName) rows.push(["Name", fullName]);

  if (state.notification?.type) {
    const label =
      NOTIFICATION_LABELS[state.notification.type] ||
      cap(state.notification.type);
    const detail = state.notification.email
      ? state.notification.email
      : state.notification.phone
        ? `+1${state.notification.phone}`
        : label;
    rows.push(["Notify", detail]);
  }

  rows.push(["Notes", state.special_note || "—"]);
  return rows;
};

export const buildForklySuggestionRows = (suggestion, ctx) => {
  if (!suggestion) return [];
  const rows = [];
  const st = getStationById(ctx, suggestion.food_station_id);
  if (st) rows.push(["Station", st.name]);
  if (st?.is_multi && suggestion.sub_station_id) {
    const sub = getSubStationById(ctx, suggestion.food_station_id, suggestion.sub_station_id);
    if (sub) rows.push(["Counter", sub.name]);
  }
  rows.push(...groupedItemRows({ options_id: suggestion.options_id }, ctx));
  if (suggestion.count > 1) rows.push(["Ordered", `${suggestion.count} times`]);
  return rows;
};

export const getForklyStepView = (stepId, ctx, state, runtime) => {
  switch (stepId) {
    case "ask_order_mode": {
      return {
        messages: [
          "How do you want to order?",
          "Type it, or just talk — I'll take it hands-free.",
        ],
        chips: [
          { label: "Type my order", value: "__text__" },
          { label: "Order by voice", value: "__voice__", variant: "confirm" },
        ],
      };
    }
    case "welcome": {
      const firstName =
        ctx.customer?.first_name ||
        splitFullName(ctx.customer?.full_name || "")[0] ||
        "";
      const greeting = firstName
        ? say("greeting.returning_named", { name: firstName })
        : say("greeting.returning");

      if ((ctx.stations || []).length === 0) {
        return {
          messages: [greeting, say("stations.none_open")],
          chips: [],
        };
      }

      if (isSuggestionRepeatable(ctx)) {
        return {
          messages: [
            greeting,
            say("order.repeat_prompt"),
            {
              card: true,
              title: "Your usual",
              rows: buildForklySuggestionRows(ctx.suggestion, ctx),
            },
          ],
          chips: [
            { label: line("buttons.yes"), value: "__repeat__" },
            { label: line("buttons.start_fresh"), value: "__new__" },
          ],
        };
      }
      return {
        messages: [greeting, say("order.start")],
        chips: [],
        autoAdvance: true,
      };
    }
    case "pick_station": {
      const stations = ctx.stations || [];
      if (stations.length === 0) {
        return { messages: [say("stations.none_open")], chips: [] };
      }
      if (stations.length === 1) {
        return {
          messages: [say("stations.only_one", { name: `**${stations[0].name}**` })],
          chips: [],
          autoAdvance: true,
          autoValue: stations[0].id,
        };
      }
      return {
        messages: [say("stations.choose")],
        chips: stations.map((s) => ({ label: s.name, value: s.id })),
      };
    }
    case "pick_sub_station": {
      const st = getStationById(ctx, state.food_station_id);
      const subs = st?.sub_stations || [];
      return {
        messages: [
          say("stations.substations", { stationName: st?.name || "This station" }),
        ],
        chips: subs.map((s) => ({ label: s.name, value: s.id })),
      };
    }
    case "pick_dining": {
      const dining = ctx.diningOptions || [];
      if (dining.length === 0) {
        return { messages: [say("options.loading")], chips: [], waiting: true };
      }
      if (dining.length === 1) {
        return {
          messages: [say("options.only_one", { name: `**${dining[0].name}**` })],
          chips: [],
          autoAdvance: true,
          autoValue: dining[0].id,
        };
      }
      return {
        messages: [say("options.choose")],
        chips: dining.map((d) => ({ label: d.name, value: d.id })),
      };
    }
    case "pick_menu_group": {
      const groups = getMenuGroups(ctx);
      const group = groups[runtime.menuGroupIndex];
      if (!group) return { messages: [], chips: [], autoAdvance: true };

      const groupName = group.group.group_name;
      const allAllowed = getAllowedItems(group, []);
      if (allAllowed.length === 0) {
        return { messages: [], chips: [], autoAdvance: true };
      }
      const safeAllowed = getAllowedItems(group, state.allergies);
      const hasAllergenItems = allAllowed.length > safeAllowed.length;
      const hasRealAllergies =
        Array.isArray(state.allergies) &&
        state.allergies.length > 0 &&
        !(state.allergies.length === 1 && state.allergies[0] === "none");
      const effectiveAllowed = runtime.showAllergenItems ? allAllowed : safeAllowed;
      const isMulti = !!group.group.allow_multiple;
      const max = group.group.max_multiple || 1;
      const toggleChips =
        hasAllergenItems && hasRealAllergies
          ? [
              {
                label: fill(
                  runtime.showAllergenItems
                    ? line("allergy.toggle_hide")
                    : line("allergy.toggle_show"),
                  { groupName },
                ),
                value: "__toggle_allergens__",
                action: "toggle_allergens",
              },
            ]
          : [];

      if (effectiveAllowed.length === 0) {
        return {
          messages: [
            say("allergy.none_safe", { groupName }),
            hasAllergenItems
              ? say("allergy.offer_show")
              : say("group.nothing_else"),
          ],
          chips: toggleChips,
          items: [],
          multi: isMulti,
          multiMax: max,
        };
      }
      if (effectiveAllowed.length === 1 && toggleChips.length === 0) {
        return {
          messages: [
            say("group.only_item", {
              itemName: `**${effectiveAllowed[0].name}**`,
              groupName,
            }),
          ],
          chips: [],
          autoAdvance: true,
          autoValue: isMulti ? [effectiveAllowed[0].id] : effectiveAllowed[0].id,
        };
      }
      return {
        messages: [
          isMulti
            ? say("group.choose_max", { groupName, max })
            : say("group.choose", { groupName }),
        ],
        chips: toggleChips,
        items: effectiveAllowed.map((it) => ({
          label: it.name,
          value: it.id,
          image_name: it.image_name,
        })),
        multi: isMulti,
        multiMax: max,
      };
    }
    case "confirm_allergies": {
      const list = (state.allergies || [])
        .map((a) => allergiesNameMapper[a] || a)
        .join(", ");
      return {
        messages: [say("allergy.saved", { list }), say("allergy.confirm")],
        chips: [
          { label: line("buttons.yes"), value: "__yes__" },
          { label: line("buttons.update"), value: "__change__" },
        ],
      };
    }
    case "ask_allergies": {
      return {
        messages: [say("allergy.ask"), say("allergy.instructions")],
        chips: allergiesOptions.map((a) => ({ label: a.label, value: a.value })),
        multi: true,
        noneValue: "none",
      };
    }
    case "confirm_name": {
      const first = splitFullName(ctx.customer?.full_name || "")[0];
      return {
        messages: [say("name.first_onfile", { name: first })],
        chips: [
          { label: line("buttons.yes"), value: "__use_default__" },
          { label: line("buttons.change"), value: "__use_other__" },
        ],
      };
    }
    case "ask_name": {
      return {
        messages: [say("name.first_ask")],
        input: { placeholder: "First name", initialValue: "" },
      };
    }
    case "confirm_surname": {
      const last = splitFullName(ctx.customer?.full_name || "")[1];
      return {
        messages: [say("name.last_onfile", { name: last })],
        chips: [
          { label: line("buttons.yes"), value: "__use_default__" },
          { label: line("buttons.change"), value: "__use_other__" },
        ],
      };
    }
    case "ask_surname": {
      return {
        messages: [say("name.last_ask")],
        input: { placeholder: "Last name", initialValue: "" },
      };
    }
    case "ask_notification_type": {
      const opts = ctx.notificationOptions || [];
      const savedChannels = ctx.customer?.notification_channels;
      const enabledChannels = Array.isArray(savedChannels) ? savedChannels : null;
      const available = opts.filter((o) => {
        if (o.title === "telegram" && !ctx.customer?.telegram_chat_id) return false;
        if (enabledChannels && !enabledChannels.includes(o.title)) return false;
        return true;
      });
      if (available.length === 0) {
        return { messages: [say("notify.none")], chips: [] };
      }
      return {
        messages: [say("notify.ask")],
        chips: available.map((o) => ({
          label: NOTIFICATION_LABELS[o.title] || cap(o.title),
          value: o.title,
        })),
      };
    }
    case "confirm_notification_email": {
      return {
        messages: [say("notify.email_onfile", { email: ctx.customer?.email })],
        chips: [
          { label: line("buttons.yes"), value: "__use_default__" },
          { label: line("buttons.change"), value: "__use_other__" },
        ],
      };
    }
    case "ask_notification_email": {
      return {
        messages: [say("notify.email_ask")],
        input: { placeholder: "you@example.com", initialValue: "" },
      };
    }
    case "confirm_notification_phone": {
      return {
        messages: [say("notify.phone_onfile", { phone: ctx.customer?.phone })],
        chips: [
          { label: line("buttons.yes"), value: "__use_default__" },
          { label: line("buttons.change"), value: "__use_other__" },
        ],
      };
    }
    case "ask_notification_phone": {
      return {
        messages: [say("notify.phone_ask")],
        input: {
          placeholder: "10-digit phone number",
          initialValue: "",
          digitsOnly: true,
          maxLength: 10,
        },
      };
    }
    case "subscribe_push": {
      return { messages: [say("notify.push_enabling")], chips: [], waiting: true };
    }
    case "check_telegram": {
      return { messages: [say("notify.telegram")], chips: [], autoAdvance: true };
    }
    case "ask_special_note": {
      return {
        messages: [say("notes.ask")],
        chips: [{ label: "Skip", value: "__skip__", action: "skip" }],
        input: {
          placeholder: "Add a note for the kitchen",
          initialValue: "",
          maxLength: 100,
        },
      };
    }
    case "summary": {
      return {
        messages: [
          say("review.header"),
          {
            card: true,
            title: "Your order",
            rows: buildForklySummaryRows(state, ctx),
          },
        ],
        chips: [
          { label: "Place order", value: "__confirm__", action: "confirm" },
          { label: "Start over", value: "__restart__", action: "restart" },
        ],
      };
    }
    case "submitting": {
      return { messages: [say("submit.placing")], chips: [], waiting: true };
    }
    case "done": {
      return {
        messages: [say("submit.done")],
        chips: [
          { label: "Make another order", value: "__restart__", action: "restart" },
        ],
      };
    }
    default:
      return { messages: [], chips: [] };
  }
};

export const labelForForklyChip = (stepId, value, ctx, runtime) => {
  if (value === "__text__") return "Type my order";
  if (value === "__voice__") return "Order by voice";
  if (value === "__skip__") return "Skip";
  if (value === "__confirm__") return "Place order";
  if (value === "__restart__") return "Start over";
  if (value === "__repeat__") return line("buttons.yes");
  if (value === "__new__") return line("buttons.start_fresh");
  switch (stepId) {
    case "pick_station":
      return getStationById(ctx, value)?.name || String(value);
    case "pick_sub_station": {
      for (const st of ctx.stations || []) {
        const found = (st.sub_stations || []).find((s) => +s.id === +value);
        if (found) return found.name;
      }
      return String(value);
    }
    case "pick_dining":
      return getDiningById(ctx, value)?.name || String(value);
    case "pick_menu_group": {
      const groups = getMenuGroups(ctx);
      if (Array.isArray(value)) {
        return value
          .map((v) => getItemById(groups, v)?.item?.name)
          .filter(Boolean)
          .join(", ");
      }
      return getItemById(groups, value)?.item?.name || String(value);
    }
    case "ask_allergies": {
      if (Array.isArray(value)) {
        return value.map((v) => allergiesNameMapper[v] || v).join(", ");
      }
      return allergiesNameMapper[value] || String(value);
    }
    case "ask_notification_type":
      return NOTIFICATION_LABELS[value] || cap(value);
    default:
      return String(value);
  }
};

export const FORKLY_PHRASES = {
  placingOrder: () => say("submit.placing"),
  orderPlacedWithId: (id) => say("submit.done_id", { id }),
  orderPlaced: () => say("submit.done"),
  orderFailedSuffix: () => say("error.retry_or_restart"),
  bootstrapError: () => say("error.start_failed"),
  pushFailRetry: (reason) => say("notify.error_retry", { reason }),
  doneMultiLabel: () => line("buttons.done"),
  noAllergiesLabel: () => line("buttons.done"),
};
